import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();

  const pathname = location.pathname;
  const loginUrl = 'https://real-frontend2026.onrender.com/';

  const isActive = (path) => pathname === path;
  const isContactGroupActive = ['/contato', '/denuncia', '/fornecedor'].includes(pathname);

  const navItems = useMemo(
    () => [
      { label: 'Início', path: '/' },
      { label: 'Nossa história', path: '/about' },
      { label: 'Compliance', path: '/etica' },
      { label: 'Transparência', path: '/transparencia' },
      { label: 'Serviços', path: '/servicos' },
      { label: 'Trabalhe Conosco', path: '/trabalheconosco' }
    ],
    []
  );

  const megaColumns = [
    {
      title: 'Relacionamento',
      description: 'Canais institucionais para contato, atendimento e comunicação.',
      items: [
        {
          label: 'Fale Conosco',
          path: '/contato',
          text: 'Entre em contato com a nossa equipe para informações, dúvidas e oportunidades.'
        },
        {
          label: 'Denúncias',
          path: '/denuncia',
          text: 'Canal voltado ao registro responsável de ocorrências e relatos.'
        }
      ]
    },
    {
      title: 'Parcerias',
      description: 'Fluxos de relacionamento com parceiros e fornecedores.',
      items: [
        {
          label: 'Pré-Cadastro de Fornecedor',
          path: '/fornecedor',
          text: 'Inicie seu relacionamento comercial com a RealEnergy por meio do cadastro.'
        }
      ]
    }
  ];

  const desktopLinkStyle = (path) => ({
    position: 'relative',
    textDecoration: 'none',
    color: isActive(path) ? '#0d4fb3' : '#0d2e63',
    fontWeight: isActive(path) ? 800 : 650,
    fontSize: '0.92rem',
    letterSpacing: '0.1px',
    padding: '10px 0',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    transition: 'all 0.25s ease'
  });

  const mobileLinkStyle = (path) => ({
    display: 'block',
    textDecoration: 'none',
    color: isActive(path) ? '#0d4fb3' : '#0d2e63',
    fontWeight: isActive(path) ? 800 : 650,
    fontSize: '1rem',
    padding: '14px 0',
    borderBottom: '1px solid rgba(13, 46, 99, 0.08)'
  });

  const underlineStyle = {
    position: 'absolute',
    left: 0,
    bottom: '-8px',
    width: '100%',
    height: '2px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #0d4fb3, #0a3d91)'
  };

  const megaCardHover = {
    y: -4,
    boxShadow: '0 22px 44px rgba(13, 46, 99, 0.08)'
  };

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        width: '100%',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        background: 'rgba(255,255,255,0.9)',
        borderBottom: '1px solid rgba(13, 46, 99, 0.08)',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)'
      }}
    >
      <div
        style={{
          maxWidth: '1320px',
          margin: '0 auto',
          padding: '0 24px'
        }}
      >
        <nav
          style={{
            minHeight: '82px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px'
          }}
        >
          <Link
            to="/"
            aria-label="Ir para a página inicial"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexShrink: 0
            }}
          >
            <motion.img
              whileHover={{ scale: 1.025 }}
              transition={{ duration: 0.25 }}
              src="/assets/images/logo-real-energy-121x45.png"
              alt="RealEnergy"
              style={{
                width: '142px',
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Link>

          <div
            className="d-none d-lg-flex"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: '22px',
              flex: 1,
              minWidth: 0
            }}
          >
            {navItems.map((item) => (
              <motion.div key={item.path} whileHover={{ y: -2 }}>
                <Link to={item.path} style={desktopLinkStyle(item.path)}>
                  {item.label}
                  {isActive(item.path) && <motion.span layoutId="navbar-active-line" style={underlineStyle} />}
                </Link>
              </motion.div>
            ))}

            <div
              style={{ position: 'relative' }}
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <motion.button
                whileHover={{ y: -2 }}
                type="button"
                onClick={() => setMegaOpen((prev) => !prev)}
                style={{
                  position: 'relative',
                  background: 'transparent',
                  border: 'none',
                  padding: '10px 0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  color: isContactGroupActive ? '#0d4fb3' : '#0d2e63',
                  fontWeight: isContactGroupActive ? 800 : 650,
                  fontSize: '0.92rem',
                  whiteSpace: 'nowrap'
                }}
              >
                Contatos
                <motion.span
                  animate={{ rotate: megaOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ fontSize: '0.78rem' }}
                >
                  ▾
                </motion.span>

                {isContactGroupActive && <motion.span layoutId="navbar-active-line" style={underlineStyle} />}
              </motion.button>

              <AnimatePresence>
                {megaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 14, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 14, scale: 0.985 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 14px)',
                      right: 0,
                      left: 'auto',
                      width: 'min(680px, calc(100vw - 48px))',
                      background: 'rgba(255,255,255,0.98)',
                      backdropFilter: 'blur(18px)',
                      WebkitBackdropFilter: 'blur(18px)',
                      border: '1px solid rgba(13, 46, 99, 0.08)',
                      borderRadius: '22px',
                      boxShadow: '0 28px 80px rgba(15, 23, 42, 0.14)',
                      padding: '16px'
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '14px'
                      }}
                    >
                      {megaColumns.map((column) => (
                        <div
                          key={column.title}
                          style={{
                            background: '#ffffff',
                            borderRadius: '18px',
                            border: '1px solid rgba(13, 46, 99, 0.06)',
                            padding: '16px'
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.76rem',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: '1.1px',
                              color: '#b7832f',
                              marginBottom: '10px'
                            }}
                          >
                            {column.title}
                          </div>

                          <div
                            style={{
                              color: '#0d2e63',
                              fontWeight: 800,
                              fontSize: '1rem',
                              lineHeight: 1.35,
                              marginBottom: '10px'
                            }}
                          >
                            {column.description}
                          </div>

                          <div
                            style={{
                              display: 'grid',
                              gap: '10px',
                              marginTop: '12px'
                            }}
                          >
                            {column.items.map((item) => (
                              <motion.div key={item.path} whileHover={megaCardHover} transition={{ duration: 0.22 }}>
                                <Link
                                  to={item.path}
                                  onClick={() => setMegaOpen(false)}
                                  style={{
                                    textDecoration: 'none',
                                    display: 'block',
                                    background: isActive(item.path) ? '#f4f8ff' : '#fbfcfe',
                                    border: `1px solid ${
                                      isActive(item.path)
                                        ? 'rgba(13, 79, 179, 0.16)'
                                        : 'rgba(13, 46, 99, 0.06)'
                                    }`,
                                    borderRadius: '14px',
                                    padding: '14px'
                                  }}
                                >
                                  <div
                                    style={{
                                      color: isActive(item.path) ? '#0d4fb3' : '#0d2e63',
                                      fontWeight: 800,
                                      fontSize: '0.95rem',
                                      marginBottom: '6px'
                                    }}
                                  >
                                    {item.label}
                                  </div>

                                  <div
                                    style={{
                                      color: '#607089',
                                      lineHeight: 1.55,
                                      fontSize: '0.88rem'
                                    }}
                                  >
                                    {item.text}
                                  </div>
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div
            className="d-none d-lg-flex"
            style={{
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <motion.a
              whileHover={{ y: -2, scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              href={loginUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px 20px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #0d4fb3, #0a3d91)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.93rem',
                boxShadow: '0 18px 36px rgba(13, 79, 179, 0.22)'
              }}
            >
              Entrar
            </motion.a>
          </div>

          <button
            type="button"
            className="d-lg-none"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              border: '1px solid rgba(13, 46, 99, 0.1)',
              background: '#ffffff',
              display: 'inline-flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
              flexShrink: 0
            }}
          >
            <span style={{ width: '20px', height: '2px', background: '#0d2e63', borderRadius: '99px' }} />
            <span style={{ width: '20px', height: '2px', background: '#0d2e63', borderRadius: '99px' }} />
            <span style={{ width: '20px', height: '2px', background: '#0d2e63', borderRadius: '99px' }} />
          </button>
        </nav>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="d-lg-none"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              style={{ paddingBottom: '22px' }}
            >
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '22px',
                  padding: '20px 20px 14px',
                  border: '1px solid rgba(13, 46, 99, 0.08)',
                  boxShadow: '0 18px 48px rgba(15, 23, 42, 0.08)'
                }}
              >
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    style={mobileLinkStyle(item.path)}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}

                <div
                  style={{
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(13, 46, 99, 0.08)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: 800,
                      color: '#0d2e63',
                      marginBottom: '12px'
                    }}
                  >
                    Contatos
                  </div>

                  <div style={{ display: 'grid', gap: '12px' }}>
                    <Link
                      to="/contato"
                      onClick={() => setMenuOpen(false)}
                      style={{
                        textDecoration: 'none',
                        color: pathname === '/contato' ? '#0d4fb3' : '#5f7088',
                        fontWeight: pathname === '/contato' ? 800 : 600
                      }}
                    >
                      Fale Conosco
                    </Link>

                    <Link
                      to="/denuncia"
                      onClick={() => setMenuOpen(false)}
                      style={{
                        textDecoration: 'none',
                        color: pathname === '/denuncia' ? '#0d4fb3' : '#5f7088',
                        fontWeight: pathname === '/denuncia' ? 800 : 600
                      }}
                    >
                      Denúncias
                    </Link>

                    <Link
                      to="/fornecedor"
                      onClick={() => setMenuOpen(false)}
                      style={{
                        textDecoration: 'none',
                        color: pathname === '/fornecedor' ? '#0d4fb3' : '#5f7088',
                        fontWeight: pathname === '/fornecedor' ? 800 : 600
                      }}
                    >
                      Pré-Cadastro de Fornecedor
                    </Link>
                  </div>
                </div>

                <div style={{ paddingTop: '18px' }}>
                  <a
                    href={loginUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      textDecoration: 'none',
                      width: '100%',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '14px 18px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #0d4fb3, #0a3d91)',
                      color: '#ffffff',
                      fontWeight: 800,
                      boxShadow: '0 18px 36px rgba(13, 79, 179, 0.18)'
                    }}
                  >
                    Entrar no dashboard
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

export default Navbar;